import "./instrument";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import AIChatWidget from "./AIChatWidget";
import "@aichat/ui/globals.css";
import "./index.css";
import "@xyflow/react/dist/style.css";

import { env } from "./lib/env";
import { WidgetSelectorAttributes } from "./lib/constants";
import { initializeWidget } from "./api/widget.api";
import { WidgetConfig } from "@aichat/types/widget";
import { defaultWidgetConfig } from "@aichat/constants/widget";
import {
  constructWebWidgetFlagsmithTrait,
  FlagsmithProvider,
  flagsmith,
} from "@aichat/aichat-lib";
import { WidgetThemeType } from "@aichat/types/index";

class Widget {
  private root: HTMLElement | null = null;
  private options: WidgetConfig = defaultWidgetConfig;

  constructor() {
    if (env.mode === "production") {
      this.init();
    }
  }

  async init() {
    let apiKey: string | null = null;
    let organizationId: string | null = null;
    let isPlayground = false;
    let isAlwaysOpen = false;
    let userId: string | null = null;

    if (env.viteModeIsProd) {
      const scriptElement = document.getElementById(
        WidgetSelectorAttributes.id,
      );
      if (!scriptElement) throw new Error("AI Chat Script not mounted");

      // Check if elements are valid
      apiKey = scriptElement.getAttribute(
        WidgetSelectorAttributes.apiKeyAttribute,
      );
      organizationId = scriptElement.getAttribute(
        WidgetSelectorAttributes.orgIdAttribute,
      );
      isPlayground =
        scriptElement.getAttribute(
          WidgetSelectorAttributes.isPlaygroundAttribute,
        ) === "true";
      isAlwaysOpen =
        scriptElement.getAttribute(
          WidgetSelectorAttributes.isAlwaysOpenAttribute,
        ) === "true";
      userId = scriptElement.getAttribute(
        WidgetSelectorAttributes.userIdAttribute,
      );
    } else {
      apiKey = env.widgetApiKey;
      organizationId = env.widgetOrgId;
    }

    if (!apiKey || !organizationId)
      throw new Error(
        `Invalid Configuration. Make sure you have ${WidgetSelectorAttributes.apiKeyAttribute} and ${WidgetSelectorAttributes.orgIdAttribute} in your script tags`,
      );
    // Call api to get widget config
    const { config, messagePlaceholder, project, additionalChannels } =
      await initializeWidget({
        apiKey,
        orgId: organizationId,
      });
    this.options = {
      ...this.options,
      ...(additionalChannels
        ? { additionalChannels }
        : { additionalChannels: this.options.additionalChannels }),
      isThemeSleek: config.theme === WidgetThemeType.sleek,
      ...config,
      ...(messagePlaceholder && { placeholderMessage: messagePlaceholder }),
      ...(project?.chatbotDisplayName && {
        chatbotDisplayName: project?.chatbotDisplayName,
      }),
      supportedLanguages: project?.supportedLanguages,
      productCarouselButtonPayload: project?.meta?.productCarouselButtonLabel,
    };
    // Create container if it doesn't exist
    if (!this.root) {
      this.root = document.createElement("div");
      this.root.id = "ai-chat-widget-container";
      document.body.appendChild(this.root);
    }

    createRoot(this.root).render(
      <StrictMode>
        <FlagsmithProvider
          options={{
            environmentID: env.flagsmithEnvironmentId,
            api: env.flagsmithApi,
            identity: constructWebWidgetFlagsmithTrait({
              apiKey,
              orgId: organizationId,
            }),
          }}
          flagsmith={flagsmith.default}
        >
          <AIChatWidget
            config={this.options}
            apiKey={apiKey}
            organizationId={organizationId}
            isPlayground={isPlayground}
            isAlwaysOpen={isAlwaysOpen}
            projectId={project.id}
            deepgramApiKey={env.deepgramApiKey}
            heygenApiKey={env.heygenApiKey}
            userId={userId}
          />
        </FlagsmithProvider>
      </StrictMode>,
    );
  }

  destroy() {
    if (this.root) {
      this.root.remove();
      this.root = null;
    }
  }
}

// Create and export a single instance
const widget = new Widget();

export default widget;

// For UMD builds (global variable)
if (typeof window !== "undefined") {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).AIChatWidget = widget;
}

if (env.viteModeIsDev) {
  widget.init();
}
